<template>
  <b-card :title="$t(`message.pendingoffers`)">
    <b-card-body class="px-0">
      <div v-if="!emailSubmitted" class="mb-2">
        <b-form-group :label="$t(`message.savedofferslabel`)" label-for="emailInput">
          <b-input-group>
            <b-form-input
              id="emailInput"
              v-model.trim="email"
              type="email"
              placeholder="example@example.com"
              @input="clearErrMsg"
              @keyup.enter="submitEmail"
            />
            <b-input-group-append>
              <b-button variant="primary" @click="submitEmail" :disabled="btnLoading">{{$t('message.continue')}}</b-button>
            </b-input-group-append>
          </b-input-group>
          <p class="text-danger mt-1">{{ modalmsgOffers }}</p>
        </b-form-group>
      </div>

      <div v-if="emailSubmitted">
        <!-- Sorting -->
        <div class="d-flex justify-content-between flex-wrap">
          <b-form-group
            label="Sort"
            label-size="sm"
            label-align-sm="left"
            label-cols-sm="2"
            label-for="sortBySelect"
            class="mr-1 mb-md-0 align-items-center"
          >
            <b-input-group size="sm">
              <b-form-select
                id="sortBySelect"
                v-model="sortBy"
                :options="sortOptions"
              >
                <template #first>
                  <option value="">none</option>
                </template>
              </b-form-select>
              <b-form-select
                v-model="sortDesc"
                size="sm"
                :disabled="!sortBy"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>

          <!-- Filter -->
          <b-form-group
            label="Filter"
            label-cols-sm="2"
            label-align-sm="left"
            label-size="sm"
            label-for="filterInput"
            class="mb-0 align-items-center"
          >
            <b-input-group size="sm">
              <b-form-input
                id="filterInput"
                v-model="filter"
                type="search"
                placeholder="Type to Search"
                debounce="500"
              />
              <b-input-group-append>
                <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </div>
      </div>
    </b-card-body>

    <div v-if="emailSubmitted">
      <b-card no-body>
        <b-table
          striped
          responsive
          class="position-relative"
          :per-page="perPage"
          :current-page="currentPage"
          :items="items"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          :filter="filter"
          :filter-included-fields="filterOn"
          @filtered="onFiltered"
          :busy="isBusy"
        >
          <template #table-busy>
            <div class="d-flex align-items-center justify-content-around my-2">
              <b-spinner variant="dark" style="width: 3rem; height: 3rem;"></b-spinner>
              <b-spinner variant="dark" style="width: 3rem; height: 3rem;"></b-spinner>
              <b-spinner variant="dark" style="width: 3rem; height: 3rem;"></b-spinner>
            </div>
          </template>
          <template #cell(download_offer)="data">
            <b-dropdown :id="'dropdown-offer' + data.item.policyID" size="sm" text="Language" class="m-md-2">
              <b-dropdown-item
                target="_blank"
                :href="'https://quote.safetravel.today/offer.php?offerID=' + data.item.policyID + '&lang=gr'"
              >
                <sub>GR</sub> Greek
              </b-dropdown-item>
              <b-dropdown-item
                target="_blank"
                :href="'https://quote.safetravel.today/offer.php?offerID=' + data.item.policyID + '&lang=en'"
              >
                <sub>EN</sub> English
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <!-- issue policy -->
          <template #cell(issue_policy)="data">
            <b-dropdown block variant="success" :id="'dropdown-quote'+data.item.policyID" size="sm" text="Issue" class="m-md-2" @show="createQuote(data.item.from_app, data.item.issue_policy)">
                <b-dropdown-item variant="success" to='forms/quotation' v-if="infoQuote">{{data.item.offer_name}} Quotation</b-dropdown-item>
                <b-dropdown-item variant="danger" v-else>Quotation for this offer is not available</b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
          <b-form-group
            label="Per Page"
            label-cols="6"
            label-align="left"
            label-size="sm"
            class="text-nowrap mb-md-0 mr-1 align-items-center"
          >
            <b-form-select v-model="perPage" size="sm" inline :options="pageOptions" />
          </b-form-group>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mb-0 align-items-center"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </div>
        </b-card-body>
      </b-card>
    </div>
  </b-card>
</template>

<script>
import { products } from '@/products'
import {
  BTable,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BCardBody,
  BDropdown,
  BDropdownItem,
  BSpinner,
  BCard
} from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BCardBody,
    BDropdown,
    BDropdownItem,
    BSpinner,
  },
  metaInfo: {
    title: 'Saved Offers | Safetravel.today - Making Travel Insurance Easy',
    meta: [
      { charset: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { 
        name: 'description', 
        content: 'View and manage your saved travel insurance offers on Safetravel.today. Compare options or continue with your stored details to generate a new quotation.' 
      },
      { 
        name: 'keywords', 
        content: 'saved offers, travel insurance offers, insurance quotation, manage quotes, compare travel insurance, Safetravel.today' 
      },
      { property: 'og:title', content: 'Safetravel.today - Manage Your Saved Travel Insurance Offers' },
      { property: 'og:description', content: 'Access your stored travel insurance offers and easily continue to quotation using your saved details.' },
      { property: 'og:image', content: 'https://safetravel.today/images/og-image.png' },
      { property: 'og:type', content: 'website' },
      { name: 'robots', content: 'index, follow' },
    ],
    script: [
      {
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Safetravel.today',
          url: 'https://safetravel.today',
          logo: 'https://safetravel.today/logo.png',
          sameAs: [
            'https://www.facebook.com/safetraveltoday',
            'https://www.linkedin.com/company/safe-travel',
            'https://x.com/safetraveltoday'
          ],
          description: 'Safetravel.today is a travel insurance company offering secure, flexible, and affordable coverage for safe journeys worldwide.'
        },
      },
    ],
  },
  data() {
    return {
      btnLoading: false,
      infoQuote: false,
      modalmsgOffers: '',
      email: '',
      emailSubmitted: false,
      isBusy: true,
      perPage: 5,
      pageOptions: [5, 10, 25, 50, 100],
      totalRows: 0,
      currentPage: 1,
      sortBy: 'date',
      sortDesc: true,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      items: [],
      fields: [
        { key: 'policyID', label: 'Policy ID', sortable: true },
        { key: 'offer_name', label: 'Offer Name' },
        { key: 'policy_details', label: 'Policy Details', sortable: true },
        { key: 'date_of_travel', label: 'Date of Travel', sortable: true, tdClass: 'text-nowrap' },
        { key: 'persons', label: 'Persons', sortable: true },
        { key: 'download_offer', label: 'Download Offer' },
        { key: 'issue_policy', label: 'Issue Policy' },
        { key: 'date', label: 'Date', sortable: true, tdClass: 'text-nowrap' },
      ],
      products,
    }
  },
  computed: {
    agentParam() {
      return this.$store.state.app.agent
    },
    sortOptions() {
      return this.fields.filter(f => f.sortable).map(f => ({ text: f.label, value: f.key }))
    },
  },
  methods: {
    clearErrMsg() {
      this.modalmsgOffers=''
    },
    submitEmail() {
      this.items = [];
      this.totalRows = 0;
      this.modalmsgOffers = ''
      const trimmedEmail = this.email.trim()
      if (!trimmedEmail) {
        this.modalmsgOffers = this.$t('message.pleaseenteremail') || 'Please enter your email address'
        return
      }
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(trimmedEmail)) {
        this.emailSubmitted = false
        this.modalmsgOffers = this.$t('message.entervalidemail')
        return
      }
      this.getOffers(0, 200)
    },
    createQuote(from_app, info) {
      this.infoQuote=false;
      // remove older quote info items
      if (localStorage.getItem("quote_info_app") !== null) {
        localStorage.removeItem("quote_info_app");
      }
      if (info) {
        if (from_app) {
          this.infoQuote=true;
          // create quote_info_app
          localStorage.setItem('quote_info_app', JSON.stringify(info));
        } 
      }
    },
    async getOffers(start_limit, end_limit) {
      if (this.btnLoading) return;
      this.btnLoading = true;
      this.isBusy = true;
      const url = {
        prod: 'https://api-quote.safetravel.today/v2/app/get_pending_offers_public',
      }
      const data = {
        token: this.agentParam,
        start_limit,
        end_limit,
        email: this.email,
      }
      try {
        const res = await fetch(url.prod, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })
        .then(res => {
          if (!res.ok) throw new Error(`Get offers HTTP error ${res.status}`);
          return res.json();
        })
        if (res === 'email_not_found') {
          this.emailSubmitted = false
          this.modalmsgOffers = this.$t('message.offerslinkedtoemail')
          return;
        }
        if (res === 'not_valid') {
          this.emailSubmitted = false
          this.modalmsgOffers = this.$t('message.wewereunbl')
          return;
        }

        // now it's valid — show table
        this.emailSubmitted = true
        this.items = [] // clear old offers if any

        res.forEach(r => {
          const offer_products = []
          for (const op in r?.offer_products) {
            offer_products.push(this.products[op])
          }
          this.items.push({
            policyID: r?.policyID ?? '-',
            offer_name: r?.group_policy_name ?? '-',
            policy_details: offer_products.length ? offer_products.join(', ') : '-',
            date_of_travel: r?.date_of_travel ?? '-',
            persons: r?.number_of_insured_customers ?? '-',
            download_offer: '',
            issue_policy: r?.quote_info ?? null,
            date: r?.r_timestamp ?? '-',
            from_app: r?.app ?? null,
          })
        })

        this.totalRows = this.items.length
        this.isBusy = false
        this.sortBy = 'date'
      } catch (err) {
        console.warn('Error fetching offers:', err)
        this.modalmsgOffers = '❌ Network or server error. Please try again later.'
      } finally {
        this.btnLoading = false;
      }
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
  },
}
</script>

<style scoped>
.showListGroup {
  filter: blur(8px);
  -webkit-filter: blur(8px);
}
</style>